import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { ssoService } from '../../services/ssoService';
import { supabase } from '../../lib/supabase';


import Button from '../../components/ui/Button';

import { Shield, Mail, ArrowRight, Building2 } from 'lucide-react';
import toast, { Toaster } from 'react-hot-toast';

const SSOLogin = () => {
  const navigate = useNavigate();
  const { signIn } = useAuth();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [ssoConfig, setSsoConfig] = useState(null);
  const [showProviders, setShowProviders] = useState(false);

  const handleEmailCheck = async (e) => {
    e?.preventDefault();
    
    if (!email?.trim()) {
      toast?.error('Please enter your email address');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex?.test(email)) {
      toast?.error('Please enter a valid email address');
      return;
    }

    setLoading(true);
    try {
      // Check if domain has SSO configured
      const { data, error } = await ssoService?.getSSOConfigByDomain(email);
      
      if (error) throw error;
      
      if (data) {
        setSsoConfig(data);
        setShowProviders(true);
      } else {
        // No SSO configured, redirect to standard login
        toast?.error('No SSO configuration found for this domain. Please use standard login.');
        setTimeout(() => navigate('/login'), 2000);
      }
    } catch (error) {
      console.error('Error checking SSO configuration:', error);
      toast?.error('Failed to check SSO configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleSSOLogin = async (provider) => {
    setLoading(true);
    try {
      // Initiate SAML authentication with Supabase
      const { data, error } = await supabase?.auth?.signInWithSSO({
        domain: email?.split('@')?.[1],
        options: {
          redirectTo: `${window.location?.origin}/dashboard`
        }
      });

      if (error) throw error;

      // Log authentication attempt
      await ssoService?.logAuthAttempt(
        ssoConfig?.config_id,
        ssoConfig?.organization_id,
        null,
        email,
        provider,
        false,
        'Initiated SSO flow'
      );

      // Check if 2FA is required after SSO authentication
      // This will be handled in the callback after SSO completes
      // Store SSO context for 2FA verification
      sessionStorage.setItem('sso_2fa_pending', JSON.stringify({
        email,
        provider,
        configId: ssoConfig?.config_id,
        organizationId: ssoConfig?.organization_id
      }));

      // Redirect to SSO provider
      if (data?.url) {
        window.location.href = data?.url;
      }
    } catch (error) {
      console.error('SSO login error:', error);
      toast?.error(error?.message || 'SSO login failed');
      setLoading(false);
    }
  };

  const handleStandardLogin = () => {
    navigate('/login');
  };

  const getProviderIcon = (provider) => {
    const icons = {
      okta: '🔷',
      azure_ad: '🔵',
      google_workspace: '🔴'
    };
    return icons?.[provider] || '🔒';
  };

  const getProviderName = (provider) => {
    const names = {
      okta: 'Okta',
      azure_ad: 'Azure AD',
      google_workspace: 'Google Workspace'
    };
    return names?.[provider] || provider;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-purple-50 flex items-center justify-center p-4">
      <Toaster position="top-right" />
      <div className="w-full max-w-md">
        {/* Logo and Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary rounded-2xl mb-4">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-foreground mb-2">Enterprise SSO Login</h1>
          <p className="text-muted-foreground">Sign in with your organization account</p>
        </div>

        {/* Login Card */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
          {!showProviders ? (
            /* Email Domain Detection */
            (<form onSubmit={handleEmailCheck} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Work Email Address
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="email"
                    placeholder="you@company.com"
                    value={email}
                    onChange={(e) => setEmail(e?.target?.value)}
                    disabled={loading}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Enter your work email to detect your organization SSO provider
                </p>
              </div>
              <Button
                type="submit"
                fullWidth
                size="lg"
                loading={loading}
                disabled={loading || !email?.trim()}
                iconName="ArrowRight"
                iconPosition="right"
              >
                Continue with SSO
              </Button>
              {/* Divider */}
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-muted-foreground">or</span>
                </div>
              </div>
              {/* Standard Login Link */}
              <button
                type="button"
                onClick={handleStandardLogin}
                disabled={loading}
                className="w-full text-center text-sm text-primary hover:text-primary/80 underline-offset-4 hover:underline transition-colors"
              >
                Sign in with email and password
              </button>
            </form>)
          ) : (
            /* SSO Provider Selection */
            (<div className="space-y-5">
              {/* Detected Organization */}
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center gap-3">
                  <Building2 className="w-5 h-5 text-green-600" />
                  <div>
                    <p className="text-sm font-medium text-green-900">
                      SSO Detected
                    </p>
                    <p className="text-xs text-green-700">
                      {email?.split('@')?.[1]}
                    </p>
                  </div>
                </div>
              </div>
              {/* Provider Button */}
              <button
                onClick={() => handleSSOLogin(ssoConfig?.provider)}
                disabled={loading}
                className="w-full p-4 border-2 border-gray-200 rounded-lg hover:border-primary hover:bg-primary/5 transition-all flex items-center justify-between group"
              >
                <div className="flex items-center gap-3">
                  <span className="text-3xl">{getProviderIcon(ssoConfig?.provider)}</span>
                  <div className="text-left">
                    <p className="font-semibold text-foreground">
                      Continue with {getProviderName(ssoConfig?.provider)}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Sign in using your organization credentials
                    </p>
                  </div>
                </div>
                <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-primary transition-colors" />
              </button>
              {/* Back Button */}
              <button
                onClick={() => {
                  setShowProviders(false);
                  setSsoConfig(null);
                }}
                disabled={loading}
                className="w-full text-center text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                ← Use different email
              </button>
              {/* Standard Login Link */}
              <div className="relative my-4">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-muted-foreground">or</span>
                </div>
              </div>
              <button
                type="button"
                onClick={handleStandardLogin}
                disabled={loading}
                className="w-full text-center text-sm text-primary hover:text-primary/80 underline-offset-4 hover:underline transition-colors"
              >
                Sign in with email and password
              </button>
            </div>)
          )}
        </div>

        {/* Footer */}
        <div className="text-center mt-6 text-sm text-muted-foreground">
          <p>
            Don't have an account?{' '}
            <button
              onClick={() => navigate('/register')}
              className="text-primary hover:underline"
            >
              Sign up
            </button>
          </p>
        </div>

        {/* Security Notice */}
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-xs text-blue-800 text-center">
            <Shield className="w-4 h-4 inline mr-1" />
            Your login is secured with enterprise-grade SAML authentication
          </p>
        </div>
      </div>
    </div>
  );
};

export default SSOLogin;